import Decimal from 'decimal.js';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { getCargoOperationForCargoAndOperationType, isManualCall as isManualRotationElement } from './stowageRotationUtils';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';

export const getFormattedCargoNumber = (voyageNumber: string, cargoNumber: number, isManual: boolean): string => {
    if (!voyageNumber || !cargoNumber) {
        return '';
    }
    const prefix = isManual ? 'M' : '';
    return prefix + voyageNumber + '-' + cargoNumber.toString().padStart(3, '0');
};

export const getShortFormattedCargoNumber = (cargoNumber: number, isManual: boolean): string => {
    if (!cargoNumber) {
        return '';
    }
    const prefix = isManual ? 'M' : '';
    return prefix + cargoNumber.toString().padStart(3, '0');
};

export const isManualCargoOperation = (cargoOperation: RotationCargoOperation): boolean => {
    if (!cargoOperation?.cargo) return false;

    if (hasOrcaWebDataSource(cargoOperation.cargo)) return true;
    if (isTransferCargoOperation(cargoOperation)) return true;
    if (isLoadingOrUnloadingCargoOperation(cargoOperation) && hasOrcaWebDataSource(cargoOperation)) return true;

    return false;
};

export const hasOrcaWebDataSource = (item: { dataSource?: string }): boolean => {
    return item?.dataSource === 'OrcaWeb';
};

export const isManualCargo = (cargo: RotationCargo): boolean => {
    return isManualRotationElement(cargo);
};

/**
 * Gets the charterer name from the cargo, falling back to product's charterer name if not available.
 * @param cargo - The rotation cargo to get the charterer name from
 * @returns The charterer name or empty string if not found
 */
export const getChartererName = (cargo: RotationCargo | undefined | null): string => {
    if (!cargo) return '';
    return cargo.chartererName ?? cargo.product?.chartererName ?? '';
};

export const getRange = (minValue: number | undefined, maxValue: number | undefined): string | null => {
    // TODO: NULL-SAFETY - Logic checks for null but parameters typed as undefined. Should also check for undefined explicitly or update typing to include null
    return minValue !== null && minValue !== undefined && maxValue !== null && maxValue !== undefined
        ? minValue === maxValue
            ? `${minValue}`
            : `${minValue} / ${maxValue}`
        : null;
};

export const isLoadingOrUnloadingCargoOperation = (cargoOperation: RotationCargoOperation): boolean => {
    return ['Loading', 'Unloading'].includes(cargoOperation.operationType);
};

export const isLoadingCargoOperation = (cargoOperation: RotationCargoOperation): boolean => cargoOperation.operationType === 'Loading';
export const isUnloadingCargoOperation = (cargoOperation: RotationCargoOperation): boolean => cargoOperation.operationType === 'Unloading';

export const isTransferCargoOperation = (cargoOperation: RotationCargoOperation): boolean => {
    return cargoOperation?.operationType === 'Transfer';
};

export const isTransitOperation = (cargoOperation: RotationCargoOperation): boolean => {
    return cargoOperation.operationType === 'Transit';
};

type WeightType = 'nominated' | 'contract' | 'billOfLading';
export type OperationType = 'Loading' | 'Unloading';

export const getCargoOperationWeight = (
    stowagePlan: StowagePlan,
    cargo: RotationCargo,
    operationType: OperationType,
    weightType: WeightType,
    toBerthCall?: RotationBerthCall
): number => {
    const cargoOperations = getCargoOperationForCargoAndOperationType(stowagePlan, cargo, operationType, toBerthCall);
    const total = cargoOperations.reduce((sum, cargoOperation) => {
        let weight = new Decimal(0);
        switch (weightType) {
            case 'nominated':
                weight = new Decimal(cargoOperation.nominatedWeight ?? 0);
                break;
            case 'contract':
                weight = new Decimal(cargoOperation.contractWeight ?? 0);
                break;
            case 'billOfLading':
                weight = new Decimal(cargoOperation.billOfLadingWeight ?? 0);
                break;
        }
        return sum.plus(weight);
    }, new Decimal(0));
    return total.toNumber();
};
